import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { cn } from '@/core/lib/utils';
import type { ReactNode } from 'react';

const TEXT_COLORS = [
  { value: null, label: 'default' },
  { value: '#111827', label: 'gray' },
  { value: '#dc2626', label: 'red' },
  { value: '#ea580c', label: 'orange' },
  { value: '#ca8a04', label: 'yellow' },
  { value: '#16a34a', label: 'green' },
  { value: '#0d9488', label: 'teal' },
  { value: '#2563eb', label: 'blue' },
  { value: '#7c3aed', label: 'violet' },
  { value: '#db2777', label: 'pink' },
] as const;

const HIGHLIGHT_COLORS = [
  { value: null, label: 'none' },
  { value: '#fef08a', label: 'yellow' },
  { value: '#bbf7d0', label: 'green' },
  { value: '#bfdbfe', label: 'blue' },
  { value: '#fecaca', label: 'red' },
  { value: '#e9d5ff', label: 'purple' },
  { value: '#fed7aa', label: 'orange' },
] as const;

interface ColorPickerPopoverProps {
  type: 'text' | 'highlight';
  value: string | null;
  onChange: (color: string | null) => void;
  trigger: ReactNode;
  testId: string;
}

/** Color swatch grid popover for text color and background highlight selection. */
export function ColorPickerPopover({
  type,
  value,
  onChange,
  trigger,
  testId,
}: ColorPickerPopoverProps) {
  const { t } = useTranslation('richText');
  const colors = type === 'text' ? TEXT_COLORS : HIGHLIGHT_COLORS;

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {type === 'text' ? t('bubble.textColor') : t('bubble.highlight')}
        </TooltipContent>
      </Tooltip>

      <PopoverContent className="w-auto p-2" data-testid={testId} side="top" align="start">
        <div className="grid grid-cols-5 gap-1">
          {colors.map((color) => (
            <button
              key={color.label}
              type="button"
              aria-label={color.label}
              aria-pressed={value === color.value}
              data-testid={testId ? `${testId}-${color.label}` : undefined}
              onClick={() => onChange(color.value)}
              className={cn(
                'h-6 w-6 rounded border border-border transition-transform hover:scale-110',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                value === color.value && 'ring-2 ring-ring ring-offset-1',
                color.value === null && 'bg-transparent text-xs font-medium text-muted-foreground',
              )}
              style={color.value ? { backgroundColor: color.value } : undefined}
            >
              {color.value === null ? '✕' : null}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
